// Ingresos del técnico en Solar Hub: él cobra la instalación, el 80% es su neto.
// El 20% se reinvierte (infra, capacitaciones, herramientas) — no es ganancia neta de la plataforma.

import { baseInstalacionCotizacion, desgloseCierre } from './cierre';

const CERRADA = new Set(['instalada', 'instalado', 'completada', 'completado', 'cerrada', 'cerrado']);
const EN_OBRA = new Set(['programada', 'en_instalacion', 'en_proceso', 'asignada', 'asignado']);
const GANADA = new Set(['ganada', 'cerrada', 'asignado', 'documentos_aprobados']);

function esDelTecnico(row, claims) {
  if (!row || !claims) return false;
  const id = claims.technician_id ?? claims.user_id ?? claims.id;
  const email = String(claims.email || '').toLowerCase();
  const tid = row.technician_id ?? row.tecnicoId ?? row.technicianId;
  const temail = String(row.technician_email || row.tecnicoEmail || row.tech_email || '').toLowerCase();
  if (id != null && tid != null && String(tid) === String(id)) return true;
  if (email && temail && temail === email) return true;
  return false;
}

function estadoDeLinea(q, orden) {
  if (q?.pagado || orden?.pagado || orden?.pago_estado === 'pagada') return 'girado';
  const oe = String(orden?.estado || orden?.status || '').toLowerCase();
  if (CERRADA.has(oe)) return 'listo';
  const qs = String(q?.status || '').toLowerCase();
  if (GANADA.has(qs) || EN_OBRA.has(oe)) return 'pendiente';
  return 'abierto';
}

function quoteDeOrden(orden, quotes) {
  if (!orden?.quoteId && !orden?.quote_id) return null;
  const id = orden.quoteId ?? orden.quote_id;
  return (quotes || []).find((q) => String(q.id) === String(id)) || null;
}

function estadoConPago(local, pago) {
  if (!pago) return local;
  if (pago.estado === 'girado') return 'girado';
  if (pago.estado === 'listo') return 'listo';
  return local;
}

/** Líneas de ingreso del técnico a partir de cotizaciones, órdenes y giros persistidos. */
export function lineasIngresoTecnico(claims, { quotes = [], ordenes = [], pagos = [] } = {}) {
  const porOrden = new Map((pagos || []).map((p) => [String(p.order_id || p.orderId || ''), p]));
  const vistos = new Set();
  const lineas = [];

  for (const o of ordenes) {
    if (!esDelTecnico(o, claims)) continue;
    if (o.tipo && o.tipo !== 'instalacion') continue;
    const q = quoteDeOrden(o, quotes);
    const ins = Number(o.valorInstalacion || o.installation_total || baseInstalacionCotizacion(q) || 0);
    const d = desgloseCierre({ instalacion: ins });
    const key = `o-${o.id}`;
    vistos.add(String(q?.id || ''));
    lineas.push({
      key,
      cliente: o.cliente || q?.name || q?.company || 'Cliente',
      kwp: o.kwp ?? q?.results?.actKwp ?? null,
      instalacion: d.instalacion,
      neto: d.techEarnings,
      reinversion: d.cargoAdminTec,
      estado: estadoConPago(estadoDeLinea(q, o), porOrden.get(String(o.id))),
      origen: 'obra',
      fecha: o.fecha || o.installed_at || q?.cliente_acepto_en || q?.date,
    });
  }

  for (const q of quotes) {
    if (!esDelTecnico(q, claims)) continue;
    if (vistos.has(String(q.id))) continue;
    const ins = baseInstalacionCotizacion(q);
    const d = desgloseCierre({ instalacion: ins });
    lineas.push({
      key: `q-${q.id}`,
      cliente: q.name || q.company || 'Cliente',
      kwp: q.results?.actKwp ?? null,
      instalacion: d.instalacion,
      neto: d.techEarnings,
      reinversion: d.cargoAdminTec,
      estado: estadoDeLinea(q, null),
      origen: 'cotizacion',
      fecha: q.cliente_acepto_en || q.date || q.created_at,
    });
  }

  return lineas;
}

export function resumenIngresosTecnico(lineas) {
  const sum = (pred) => (lineas || []).filter(pred).reduce((s, l) => s + (l.neto || 0), 0);
  return {
    netoPendiente: sum((l) => l.estado === 'pendiente'),
    netoListo: sum((l) => l.estado === 'listo'),
    netoGirado: sum((l) => l.estado === 'girado'),
    netoTotal: sum((l) => l.estado !== 'abierto'),
    obras: (lineas || []).filter((l) => l.estado !== 'abierto').length,
    abiertas: (lineas || []).filter((l) => l.estado === 'abierto').length,
  };
}
